<template>
    <div>
        <multiselect
            :multiple="false"
            :options="groups"
            placeholder="Select a list"
            label="title"
            track-by="tra_id"
            select-label=""
            deselect-label=""
            v-model="selectedGroup"
            ref="groupSelect"
            :close-on-select="true"
            :internal-search="false"
            :loading="loading"
            @open="onOpen"
            @close="onClose"
            @search-change="onSearch"
        >
            <template #option="props">
                <div class="flex items-center justify-between py-0 px-2 transition-colors duration-200">
                    <div class="flex-1">
                        {{ props.option.title }}
                    </div>
                    <div v-if="props.option.description" class="text-sm text-gray-500 truncate max-w-xs">
                        {{ truncate(props.option.description, 36, "...") }}
                    </div>
                </div>
            </template>
   
            <template #afterList>
                <li ref="load" class="loader">
                    <div class="m-2">
                        <template v-if="hasNextPage">
                            Loading more lists...
                        </template>
                        <template v-else-if="!searchText && groups.length > 0">
                            No more lists to show
                        </template>
                        <template v-else-if="groups.length === 0 && !loading">
                            No lists found
                        </template>
                    </div>
                </li>
            </template>
        </multiselect>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import axios from "axios";
import _ from "lodash";

const props = defineProps({
    modelValue: {
        type: [Object, null],
        default: null,
    }
});

const emit = defineEmits(['update:modelValue']);

const groups = ref([]);
const nextPageUrl = ref(null);
const selectedGroup = ref(null);
const searchText = ref("");
const loading = ref(true);
const listId = ref(null);
const groupSelect = ref(null);
const load = ref(null);

// Observer that triggers loading more data when the loader element is visible
const observer = new IntersectionObserver(([{ isIntersecting }]) => {
    // Only trigger if intersecting, not already loading, and there is a next page
    if (isIntersecting && !loading.value && nextPageUrl.value) {
        
        // Get the scrollable container and save its scroll position
        const scrollContainer = load.value?.offsetParent;
        const scrollTop = scrollContainer?.scrollTop || 0;
        
        // Load the next page of data
        getGroups(nextPageUrl.value, searchText.value).then(() => {
            // Restore scroll position after DOM updates
            nextTick(() => {
                if (scrollContainer) {
                    scrollContainer.scrollTop = scrollTop;
                }
                
                // Check if we should disconnect the observer (no more pages)
                if (!nextPageUrl.value) {
                    observer.disconnect();
                }
            });
        });
    }
});

const value = computed(() => props.modelValue);
const hasNextPage = computed(() => nextPageUrl.value !== null);

const onOpen = async () => {
    // Always disconnect first to prevent multiple observers
    observer.disconnect();
    
    if (hasNextPage.value) {
        await nextTick();
        // Only observe if we have more pages to load
        observer.observe(load.value);
    }
};

const onClose = () => {
    observer.disconnect();
};

const getGroups = async (url = null, search = null) => {
    try {
        loading.value = true;
        
        // Check if list_id is in the URL
        let params = new URLSearchParams(document.location.search);
        if (params.has("list_id")) {
            listId.value = params.get("list_id");
        }

        const endpoint = url || route('groups.index.json'); 
        
        // Prepare request parameters
        const requestParams = {};
        if (search) {
            requestParams.search = search;
        }
        
        // Only add page parameter for first page
        if (!url) {
            requestParams.page = 1;
        }
       
        // Make the API request with proper error handling
        const response = await axios.get(endpoint, {
            params: url ? undefined : requestParams // Don't add params if we have a full URL
        });
        
        // Ensure we have the expected data structure
        if (!response.data || !Array.isArray(response.data.data)) {
            console.error("Unexpected API response format:", response.data);
            return;
        }
        
        // Update groups based on whether this is first page or pagination
        if (!url) {
            // First page - replace existing data
            groups.value = response.data.data;
        } else {
            // Pagination - append to existing data
            groups.value = [...groups.value, ...response.data.data];
        }
        
        // Update pagination URL
        nextPageUrl.value = response.data.next_page_url;
        
        // If we have a list_id, try to select that group
        if (listId.value) {
            const foundGroup = groups.value.find(group => group.tra_id === listId.value);
            if (foundGroup) {
                selectedGroup.value = foundGroup;
            }
        }
    } catch (error) {
        console.error("Error fetching groups:", error);
        if (error.response) {
            console.error("Response data:", error.response.data);
            console.error("Response status:", error.response.status);
        }
    } finally {
        loading.value = false;
    }
};

const onSearch = (search) => {
    // If search text hasn't changed, don't trigger a new search
    if (searchText.value === search) return;
    
    searchText.value = search;
    
    // Always reset pagination when search changes
    nextPageUrl.value = null;

    // Disconnect the observer during search to prevent interference
    observer.disconnect();
    
    if (search.length) {
        searchGroups(search);
    } else {
        // Clear groups before fetching to avoid duplicates
        groups.value = [];
        getGroups(null, "");
    }
};

const searchGroups = _.debounce((search) => {
    // Reset state for new search
    groups.value = [];
    nextPageUrl.value = null;
    
    // Set loading before the API call
    loading.value = true;
    
    // Perform the search with null URL to reset pagination
    getGroups(null, search);
}, 350);

const truncate = (text, length, suffix) => {
    if (text && text.length > length) {
        return text.substring(0, length) + suffix;
    } else {
        return text;
    }
};

onMounted(async () => {
    await getGroups();

    if (value.value !== null) {
        // Set selectedGroup based on found group from API
        const foundGroup = groups.value.find(
            group => group.id === value.value.id
        );
        
        if (foundGroup) {
            selectedGroup.value = foundGroup;
        }
    }
});

// Monitor selectedGroup changes
watch(selectedGroup, (newValue) => {
    emit('update:modelValue', newValue);
});

// Monitor modelValue changes
watch(value, (newValue) => {
    if (newValue === null) {
        selectedGroup.value = null;
    } else if (newValue && groups.value.length > 0) {
        // Try to find the group in our loaded groups
        const foundGroup = groups.value.find(
            group => group.id === newValue.id || group.tra_id === newValue.tra_id
        );
        
        if (foundGroup && foundGroup !== selectedGroup.value) {
            selectedGroup.value = foundGroup;
        }
    }
});
</script>

<style scoped>
/* Custom styles for the component */
.loader {
    text-align: center;
    padding: 0.5rem;
    color: #666;
}

:deep(.multiselect__spinner) {
    background: #fff;
    display: block;
    width: 16px;
    height: 16px;
    position: absolute;
    right: 20px;
    top: 11px;
    margin: 0;
    transform: rotate(0);
    border: 2px solid #e8e8e8;
    border-radius: 100%;
    border-right-color: transparent;
    box-sizing: border-box;
    transition: all 0.2s ease;
}

:deep(.multiselect__option--highlight) {
    background: #6366f1; /* Indigo-500 */
    color: white;
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
    background: #4f46e5; /* Indigo-600 */
    color: white;
}

:deep(.multiselect__tags) {
    min-height: 40px;
    padding: 0.5rem;
    border-radius: 0.375rem;
    border-color: #e2e8f0; /* Gray-200 */
}

:deep(.multiselect__input) {
    font-size: 0.875rem;
}

:deep(.multiselect__content-wrapper) {
    border-radius: 0 0 0.375rem 0.375rem;
    border-color: #e2e8f0; /* Gray-200 */
}
</style>